import { supabase } from './supabase';

// ─── Guest request CRUD ───────────────────────────────────────────────────
// A "guest request" is a creator asking another creator to be a guest on
// their show. The host can accept/decline/mark booked from their inbox.

export const REQUEST_STATUSES = {
  pending:   { label: 'Pending',   color: '#ffb101' },
  accepted:  { label: 'Accepted',  color: '#4ade80' },
  declined:  { label: 'Declined',  color: '#ff5555' },
  booked:    { label: 'Booked',    color: '#6C7FFF' },
  completed: { label: 'Completed', color: '#22c55e' },
};

// Submit a new guest request
export async function createGuestRequest(requesterId, {
  hostId,
  showId = null,
  subject,
  body,
  topics = [],
  availability = '',
  sampleUrl = '',
}) {
  if (!supabase || !requesterId) throw new Error('Not authenticated');
  if (!hostId) throw new Error('Host user required');
  if (!subject?.trim() || !body?.trim()) throw new Error('Subject and pitch are required');

  const { data, error } = await supabase
    .from('guest_requests')
    .insert({
      requester_id:  requesterId,
      host_id:       hostId,
      show_id:       showId,
      pitch_subject: subject.trim(),
      pitch_body:    body.trim(),
      topics:        (topics || []).filter(Boolean),
      availability:  availability.trim() || null,
      sample_url:    sampleUrl.trim() || null,
    })
    .select()
    .single();
  if (error) { console.error('createGuestRequest error:', error); throw error; }
  return data;
}

// Incoming requests for the current host
export async function getIncomingRequests(hostId, { status } = {}) {
  if (!supabase || !hostId) return [];
  let q = supabase
    .from('guest_requests')
    .select('*')
    .eq('host_id', hostId)
    .order('created_at', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  if (error) { console.error('getIncomingRequests error:', error); return []; }
  return data || [];
}

// Outgoing requests the current user has sent
export async function getOutgoingRequests(requesterId) {
  if (!supabase || !requesterId) return [];
  const { data, error } = await supabase
    .from('guest_requests')
    .select('*')
    .eq('requester_id', requesterId)
    .order('created_at', { ascending: false });
  if (error) { console.error('getOutgoingRequests error:', error); return []; }
  return data || [];
}

// Host responds — only the host can call this (RLS enforced)
export async function respondToRequest(requestId, hostId, { status, note = '' }) {
  if (!supabase || !hostId || !requestId) throw new Error('Missing ids');
  if (!['accepted', 'declined', 'booked', 'pending', 'completed'].includes(status)) {
    throw new Error('Invalid status');
  }
  const patch = {
    status,
    host_note: note || null,
    responded_at: status === 'pending' ? null : new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from('guest_requests')
    .update(patch)
    .eq('id', requestId)
    .eq('host_id', hostId)
    .select()
    .single();
  if (error) { console.error('respondToRequest error:', error); throw error; }
  return data;
}
